/**
 * Looped WebAudio ringtone for incoming/outgoing call phases. Self-contained on
 * purpose (mirrors playNotificationSound): two-tone chirp repeated until stopped.
 * The outgoing variant is a quieter single-tone "ringback".
 */
import { envConfig } from '#constants/envConfig';

let audioCtx: AudioContext | null = null;
let loopTimer: number | null = null;
let element: HTMLAudioElement | null = null;
let watchdog: number | null = null;

const ensureContext = (): AudioContext | null => {
  try {
    if (!audioCtx || audioCtx.state === 'closed') {
      audioCtx = new AudioContext();
    }
    if (audioCtx.state === 'suspended') {
      // Rejects asynchronously — the try/catch above never sees it, and a bare `void` would
      // leave an unhandled rejection (see playNotificationSound for the same fix).
      audioCtx.resume().catch(() => undefined);
    }
    return audioCtx;
  } catch {
    return null;
  }
};

type Tone = { frequency: number; at: number; duration: number; volume: number };

const playTone = (ctx: AudioContext, tone: Tone) => {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.type = 'sine';
  osc.frequency.value = tone.frequency;
  gain.gain.setValueAtTime(tone.volume, tone.at);
  gain.gain.exponentialRampToValueAtTime(0.001, tone.at + tone.duration);
  osc.start(tone.at);
  osc.stop(tone.at + tone.duration);
};

const playIncomingBurst = () => {
  const ctx = ensureContext();
  if (!ctx) return;
  const now = ctx.currentTime;
  playTone(ctx, { frequency: 659.25, at: now, duration: 0.25, volume: 0.14 }); // E5
  playTone(ctx, { frequency: 880, at: now + 0.28, duration: 0.25, volume: 0.14 }); // A5
  playTone(ctx, { frequency: 659.25, at: now + 0.56, duration: 0.25, volume: 0.14 });
  playTone(ctx, { frequency: 880, at: now + 0.84, duration: 0.35, volume: 0.14 });
};

const playOutgoingBurst = () => {
  const ctx = ensureContext();
  if (!ctx) return;
  const now = ctx.currentTime;
  playTone(ctx, { frequency: 440, at: now, duration: 0.8, volume: 0.05 }); // classic ringback A4
};

/**
 * In the app an incoming call plays the shipped ringtone file at full volume instead of the
 * synthesised chirp. The chirp exists for browsers, where a tab ringing loudly would be rude;
 * on a phone the same 0.14 gain on the media stream is simply inaudible, and the call has to be
 * heard across a room. Same four notes either way — the file is rendered from them.
 */
const PLAY_ATTEMPTS = 8;
const PLAY_RETRY_MS = 400;

/**
 * Two encodings of the same cycle, offered in one element so the platform picks for itself —
 * a media element plays the first <source> it can decode and ignores the rest.
 *
 * WKWebView cannot decode Ogg Vorbis at all, so on iOS a lone .ogg makes every attempt below
 * reject with "no supported source", burns the full retry budget, and lands on the quiet
 * synthesised loop — a ringtone nobody hears across a room, which is the whole reason a file
 * is shipped. Ogg stays first so Android keeps the byte-identical path it already ships; only
 * iOS falls through to the AAC copy. Both are rendered by android/branding/make-call-ringtone.py
 * from the same notes and normalised to the same peak, so the two sound alike.
 */
const buildRingtoneElement = (): HTMLAudioElement => {
  const audio = document.createElement('audio');

  for (const [src, type] of [
    ['/call-ringtone.ogg', 'audio/ogg'],
    ['/call-ringtone.m4a', 'audio/mp4'],
  ]) {
    const source = document.createElement('source');
    source.src = src;
    source.type = type;
    audio.append(source);
  }

  return audio;
};

const startFileRingtone = async (): Promise<boolean> => {
  const audio = buildRingtoneElement();
  audio.loop = true;
  audio.volume = 1;
  audio.setAttribute('playsinline', '');
  // Attached to the document on purpose: a detached element gets its playback torn down by
  // the WebView ("the media was removed from the document"), which is why the ring kept
  // stopping a moment after it started.
  audio.style.display = 'none';
  document.body.appendChild(audio);
  element = audio;

  // A call raised from a locked phone arrives before the page is visible, and a hidden page
  // is not allowed to start audio — the first attempt is refused through no fault of ours.
  // Retrying across the next few seconds catches the moment the call screen actually appears,
  // which is also the moment the sound is wanted.
  for (let attempt = 0; attempt < PLAY_ATTEMPTS; attempt += 1) {
    if (element !== audio) {
      // Ringing stopped while we were waiting.
      return false;
    }
    try {
      await audio.play();
      // Keep it ringing. Android hands audio focus to the system ringtone that woke the phone,
      // and Chrome pauses our stream when it loses focus — without this the call went quiet a
      // second after the handover and stayed quiet for the rest of the ring.
      watchdog = window.setInterval(() => {
        if (element !== audio) {
          return;
        }
        if (audio.paused) {
          void audio.play().catch(() => undefined);
        }
      }, 500);
      return true;
    } catch {
      await new Promise((resolve) => setTimeout(resolve, PLAY_RETRY_MS));
    }
  }

  // Never got permission to make a sound — leave the system ringtone alone and keep the quiet
  // synthesised loop going, so the call is at least not silent in the app.
  audio.remove();
  element = null;
  startToneLoop('incoming');
  return false;
};

const startToneLoop = (kind: 'incoming' | 'outgoing') => {
  const burst = kind === 'incoming' ? playIncomingBurst : playOutgoingBurst;
  burst();
  loopTimer = window.setInterval(burst, kind === 'incoming' ? 2_000 : 3_000);
};

/**
 * Starts ringing. Resolves to true when the shipped ringtone file is really playing — the app
 * uses that to decide whether it may silence the system notification, which is the other
 * ringtone. Silencing it before our own sound starts leaves a call ringing with no sound at all.
 */
export const startRingtone = async (kind: 'incoming' | 'outgoing'): Promise<boolean> => {
  stopRingtone();
  if (kind === 'incoming' && envConfig.IS_NATIVE) {
    return startFileRingtone();
  }
  startToneLoop(kind);
  return false;
};

export const stopRingtone = () => {
  if (loopTimer !== null) {
    window.clearInterval(loopTimer);
    loopTimer = null;
  }
  if (watchdog !== null) {
    window.clearInterval(watchdog);
    watchdog = null;
  }
  if (element) {
    element.pause();
    element.remove();
    element = null;
  }
};
